import React, { Component } from "react";
import apiProxy from "../../api/apiProxy";
import StoreZoneSceneLevelsPage from "./StoreZoneSceneLevelsPage";
import LoadingWrapper from "../LoadingWrapper";
import PageTitle from "../PageTitle";
import { ToastConsumer } from "../WithToastManager";

const ERROR_NO_STORE_ZONE_SCENES = "STORE_ZONE_NOT_CONFIGURED";

class StoreZoneSceneLevelsPageWrapper extends Component {
  state = {
    zoneSceneLevels: null,
    isLoading: true,
    storeConfiguration: {},
    store: {},
    hasLuxMeter: false
  };

  componentDidMount() {
    this.loadZoneSceneLevels();
  }

  loadZoneSceneLevels = () => {
    this.setState({ isLoading: true });

    const storeId = this.props.match.params.storeId;
    const zoneId = this.props.match.params.zoneId;
    let store = this.state.store;
    let storeConfiguration = this.state.storeConfiguration;
    let zone = null;

    apiProxy.storeLighting
      .getStoreWithConfiguration(storeId)
      .then(response => {
        store = response.store;
        storeConfiguration = response.storeConfiguration;

        zone = storeConfiguration.zones.filter(
          zone => zone.zone.zoneId.toLowerCase() === zoneId.toLowerCase()
        )[0];

        this.setState({ hasLuxMeter: zone.hasLuxMeter });
        if (zone == null) {
          // Zone doesn't exist for this store version
          const error = {
            status: 404
          };
          throw error;
        }

        return apiProxy.storeLighting.getZoneSceneLevels(
          storeId,
          zone.zone.zoneId
        );
      })
      .then(response => {
        const zoneSceneLevels = this.sortLuxBands(
          this.mapZoneSceneLevels(response)
        );

        this.setState({
          zoneSceneLevels: zoneSceneLevels,
          storeConfiguration: storeConfiguration,
          store: store,
          zoneId: zoneId,
          isLoading: false
        });
      })
      .catch(response => {
        switch (response.status) {
          case 404:
            if (
              response.body &&
              response.body.code === ERROR_NO_STORE_ZONE_SCENES
            ) {
              this.loadZoneSceneLevelDefaults(storeConfiguration, store, zone);
            } else {
              this.props.onError(response);
            }
            break;
          default:
            this.props.onError(response);
            break;
        }
      });
  };

  loadZoneSceneLevelDefaults = (storeConfiguration, store, zone) => {
    apiProxy.scene
      .getScenes()
      .then(response => {
        const zoneSceneLevels = {
          storeName: store.name,
          zone: zone.zone,
          scenes: response.scenes.map(scene => {
            return {
              scene: scene,
              value: {
                rampLevelSeconds: "",
                level: {
                  valuePercentage: ""
                }
              },
              luxBands: []
            };
          })
        };

        this.setState({
          zoneSceneLevels: zoneSceneLevels,
          storeConfiguration: storeConfiguration,
          store: store,
          isLoading: false
        });
      })
      .catch(response => {
        this.props.onError(response);
      });
  };

  sortLuxBands = zoneSceneLevels => {
    for (const sceneIndex in zoneSceneLevels.scenes) {
      const scene = zoneSceneLevels.scenes[sceneIndex];

      scene.luxBands.sort((a, b) => {
        if (a.luxLevelGreaterThan > b.luxLevelGreaterThan) {
          return -1;
        }
        if (a.luxLevelGreaterThan < b.luxLevelGreaterThan) {
          return 1;
        }
        return 0;
      });
    }

    return zoneSceneLevels;
  };

  upsertZoneSceneLevels = zoneSceneLevels => {
    const mappedSceneLevels = {
      scenes: zoneSceneLevels.scenes.map(scene => {
        return {
          scene: scene.scene.name,
          defaultValue: {
            rampTimeSeconds: scene.value.rampLevelSeconds,
            levelPercentage: scene.value.level.valuePercentage
          },
          luxBands:
            scene.luxBands.length > 0
              ? scene.luxBands.map(luxBand => {
                  return {
                    luxLevelGreaterThan: luxBand.luxLevelGreaterThan,
                    rampTimeSeconds: luxBand.rampLevelSeconds,
                    levelPercentage: luxBand.level.valuePercentage
                  };
                })
              : undefined
        };
      })
    };

    return apiProxy.storeLighting
      .upsertZoneSceneLevels(
        this.props.match.params.storeId,
        this.props.match.params.zoneId,
        mappedSceneLevels,
        this.state.storeConfiguration.version,
        zoneSceneLevels.version
      )
      .then(
        response =>
          (response = this.sortLuxBands(this.mapZoneSceneLevels(response)))
      );
  };

  createKey = () => {
    let dt = new Date().getTime();
    const uuid = "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, c => {
      const r = (dt + Math.random() * 16) % 16 | 0;
      dt = Math.floor(dt / 16);
      return (c === "x" ? r : (r & 0x3) | 0x8).toString(16);
    });
    return uuid;
  };

  mapZoneSceneLevels(zoneSceneLevels) {
    return {
      version: zoneSceneLevels.version,
      scenes: zoneSceneLevels.scenes.map(scene => {
        return {
          scene: scene.scene,
          value: {
            rampLevelSeconds: scene.defaultValue.rampTimeSeconds,
            level: {
              valuePercentage: scene.defaultValue.levelPercentage
            }
          },
          luxBands: scene.luxBands
            ? scene.luxBands.map(luxBand => {
                return {
                  key: this.createKey(),
                  luxLevelGreaterThan: luxBand.luxLevelGreaterThan,
                  rampLevelSeconds: luxBand.rampTimeSeconds,
                  level: {
                    valuePercentage: luxBand.levelPercentage
                  }
                };
              })
            : []
        };
      }),
      zone: {
        name: zoneSceneLevels.zone.name
      }
    };
  }

  render() {
    return (
      <PageTitle
        title={
          this.state.isLoading
            ? ""
            : `${this.state.zoneSceneLevels.zone.name} lighting scene`
        }
      >
        <LoadingWrapper isLoading={this.state.isLoading}>
          <ToastConsumer>
            {toastManagerContext => (
              <StoreZoneSceneLevelsPage
                store={this.state.store}
                zoneId={this.state.zoneId}
                upsertZoneSceneLevels={this.upsertZoneSceneLevels}
                zoneSceneLevels={this.state.zoneSceneLevels}
                createKey={this.createKey}
                hasLuxMeter={this.state.hasLuxMeter}
                toastManager={toastManagerContext}
                onLightingStoresChange={this.props.onLightingStoresChange}
              />
            )}
          </ToastConsumer>
        </LoadingWrapper>
      </PageTitle>
    );
  }
}

export default StoreZoneSceneLevelsPageWrapper;
